import {
  Grid,
  Typography,
  Divider,
  Box,
  Chip,
  Paper,
  TextField,
  Stack,
  List,
  ListItem,
  ListItemText,
  Button,
  Autocomplete,
  createFilterOptions
} from "@mui/material";
import { Fragment, ReactNode, useMemo, useState } from "react";
import env from "react-dotenv";
import {
  IconBuilding,
  IconBuildingStore,
  IconCalendarEvent,
  IconCalendarTime,
  IconCheckbox,
  IconClock,
  IconDeviceDesktop,
  IconMessage2Plus,
  IconPackage,
  IconUser,
  IconUserEdit,
  IconNotes,
} from "@tabler/icons-react";
import {
  CotizacionProveedoresTabla,
  FilaProductoCotizado,
  normalizarProveedoresCotizados,
  obtenerEstatusProducto,
  SeleccionProductos,
} from "modules/cotizacionesProveedor";
import { ESTATUS_COTIZACION } from "modules/bitacoraCotizacion";
import { obtenerInventariosSinDisponibilidad } from "../utils/gestionCotizacion.utils";
import type {
  Cotizacion,
  DetalleCotizacionProveedor,
  ProductoRequisicionCotizacion,
  ProveedorCotizacion,
} from "../types/gestionCotizacion.types";

const idProv = (p: any): number => Number(p.id_proveedor ?? p.id);
const etiquetaProv = (p: any): string =>
  p.nombre_comercial || p.razon_social || `Proveedor ${idProv(p)}`;
const filtrarProv = createFilterOptions<any>({
  stringify: (p) =>
    `${p.nombre_comercial ?? ""} ${p.razon_social ?? ""} ${p.contacto ?? ""} ${p.correo ?? ""}`,
  ignoreAccents: true,
  ignoreCase: true,
});
const renderOpcionProv = (props: any, option: any, selected: boolean) => (
  <li
    {...props}
    key={idProv(option)}
    style={{
      ...props.style,
      borderLeft: selected ? "2px solid #084d6e" : "2px solid transparent",
      fontWeight: selected ? 600 : 400,
    }}
  >
    <Box sx={{ textAlign: "left" }}>
      <Typography variant="body2" sx={{ fontSize: 14, fontWeight: selected ? 600 : 400 }}>
        {etiquetaProv(option)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {option.razon_social}
        {option.correo ? ` · ${option.correo}` : ""}
      </Typography>
    </Box>
  </li>
);
const renderTagsProv = (value: any[], getTagProps: any) =>
  value.map((o, index) => (
    <Chip
      {...getTagProps({ index })}
      key={idProv(o)}
      label={etiquetaProv(o)}
      size="small"
      sx={{
        backgroundColor: "#084d6e",
        color: "#fff",
        "& .MuiChip-deleteIcon": { color: "#fff" },
      }}
    />
  ));

const formatearFecha = (fecha?: string | null): string =>
  fecha
    ? new Date(fecha).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "—";

const DatoRequisicion = ({
  icono,
  etiqueta,
  valor,
}: {
  icono: ReactNode;
  etiqueta: string;
  valor?: string | null;
}) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
    <Box
      sx={{
        background: "#eef3f8",
        color: "#084d6e",
        width: 34,
        height: 34,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "5px",
      }}
    >
      {icono}
    </Box>
    <Box sx={{ minWidth: 0, textAlign: "left" }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {etiqueta}
      </Typography>
      <Typography variant="body2" fontWeight={600} noWrap title={valor ?? ""}>
        {valor || "—"}
      </Typography>
    </Box>
  </Stack>
);

const Contador = ({ icono, color, etiqueta, valor }: ContadorProps) => (
  <Stack direction="row" spacing={1}>
    <Box
      sx={{
        background: color,
        color: "#fff",
        width: 40,
        height: 40,
        flexShrink: 0,
        display: "flex",
        textAlign: { xs: "center", sm: "left" },
        borderRadius: "5px !important",
        p: 0.5,
      }}
    >
      {icono}
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {etiqueta}
      </Typography>
      <Typography variant="h5" fontWeight={600} sx={{ textAlign: "left" }}>
        {valor}
      </Typography>
    </Box>
  </Stack>
);

const ProductosRequisicionList = ({
  productos,
  esComputo,
  proveedorPorInventario,
  cantidadSeleccionada,
  onMarcarSeleccionados,
  proveedores,
  proveedoresPorProducto,
  recotizacion,
  onCambiarRecotizacion,
  onRecotizar,
  procesando,
  proveedorAutorizadoPorInventario,
  editable,
  seleccionCerrada,
  recotizacionHabilitada,
  inventariosSinDisponibilidad,
  proveedoresConDetallePorProducto,
}: {
  productos: ProductoRequisicionCotizacion[];
  esComputo: boolean;
  proveedorPorInventario: Record<number, string>;
  cantidadSeleccionada: number;
  onMarcarSeleccionados?: () => void;
  proveedores: any[];
  proveedoresPorProducto: Record<number, Set<number>>;
  recotizacion: Record<number, number[]>;
  onCambiarRecotizacion: (idInventario: number, ids: number[]) => void;
  onRecotizar: () => void;
  procesando: boolean;
  proveedorAutorizadoPorInventario: Record<number, string>;
  editable: boolean;
  seleccionCerrada: boolean;
  recotizacionHabilitada: boolean;
  inventariosSinDisponibilidad: Set<number>;
  proveedoresConDetallePorProducto: Record<number, Set<number>>;
}) => {
  const ESTATUS = obtenerEstatusProducto(esComputo);
  const completos = productos.filter((p) => p.completo).length;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h5" fontWeight={600} sx={{ textAlign: "left" }}>
          Productos de la requisición
        </Typography>
        <Chip
          size="small"
          label={`${completos}/${productos.length}`}
          sx={{
            fontWeight: 700,
            backgroundColor: completos === productos.length ? "#1dad76" : "#9e9e9e",
            color: "#fff",
          }}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "left" }}>
        {completos} de {productos.length} productos {esComputo ? "autorizados por DG" : "cotizados"}
      </Typography>
      <Divider sx={{ my: 1.5 }} />

      <List disablePadding>
        {productos.map((p) => {
          const estatus = ESTATUS[p.estatus_producto] ?? ESTATUS[0];
          const proveedor = proveedorPorInventario[p.id_inventario];
          const autorizadoCon = proveedorAutorizadoPorInventario[p.id_inventario];
          const idInventario = Number(p.id_inventario);
          const rechazadoDG = esComputo && Number(p.estatus_producto) === 2;
          // Proveedor respondió que no lo tiene
          const sinDisponibilidad = inventariosSinDisponibilidad.has(idInventario);
          const recotizable = recotizacionHabilitada && (rechazadoDG || sinDisponibilidad);
          const proveedoresExcluidos = rechazadoDG
            ? proveedoresPorProducto[idInventario]
            : proveedoresConDetallePorProducto[idInventario];

          return (
            <Fragment key={p.id_inventario}>
              <ListItem
                disableGutters
                secondaryAction={
                  <Chip
                    size="small"
                    label={estatus.label}
                    sx={{ fontWeight: 600, fontSize: 10, backgroundColor: estatus.color, color: "#fff" }}
                  />
                }
              >
                <ListItemText
                  sx={{ pr: 14 }}
                  primary={p.nombre_producto}
                  primaryTypographyProps={{ fontWeight: 500, noWrap: true, fontSize: 12 }}
                  secondary={
                    autorizadoCon
                      ? `Autorizado con: ${autorizadoCon}`
                      : proveedor
                        ? `Proveedor: ${proveedor}`
                        : undefined
                  }
                  secondaryTypographyProps={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: autorizadoCon ? "#1dad76" : "#1d80c2",
                    noWrap: true,
                  }}
                />
              </ListItem>
              {recotizable && (
                <Box sx={{ pt: 1 }}>
                  {sinDisponibilidad && !rechazadoDG && (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", textAlign: "left", color: "#bd0606", mb: 0.5 }}
                    >
                      Un proveedor no tiene disponibilidad: puedes recotizarlo con otros proveedores
                    </Typography>
                  )}
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": { paddingTop: "8px", paddingBottom: "8px" },
                      "& .MuiAutocomplete-input": {
                        paddingTop: "4px !important",
                        paddingBottom: "4px !important",
                      },
                    }}
                    options={proveedores.filter((x) => !proveedoresExcluidos?.has(idProv(x)))}
                    filterOptions={filtrarProv}
                    getOptionLabel={etiquetaProv}
                    isOptionEqualToValue={(a, b) => idProv(a) === idProv(b)}
                    value={proveedores.filter((x) =>
                      (recotizacion[p.id_inventario] ?? []).includes(idProv(x))
                    )}
                    onChange={(_, nuevos) => onCambiarRecotizacion(p.id_inventario, nuevos.map(idProv))}
                    renderOption={(props, option, { selected }) => renderOpcionProv(props, option, selected)}
                    renderTags={renderTagsProv}
                    noOptionsText="Sin proveedores disponibles"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Proveedores a recotizar"
                        placeholder={(recotizacion[p.id_inventario] ?? []).length ? "" : "Buscar proveedor..."}
                      />
                    )}
                  />
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
            </Fragment>
          );
        })}
      </List>
      {editable && !seleccionCerrada && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            startIcon={<IconCheckbox stroke={2} />}
            disabled={cantidadSeleccionada === 0}
            onClick={onMarcarSeleccionados}
            sx={{
              color: "#fff !important",
              background: "#6fa0d1",
              "&:hover": { background: "#4574a3" },
              "&:focus": { background: "#4574a3" },
            }}
          >
            Marcar como seleccionados ({cantidadSeleccionada})
          </Button>
        </Stack>
      )}
    </Box>
  );
};

interface ContadorProps {
  icono: ReactNode;
  color: string;
  etiqueta: string;
  valor: number | string;
}

interface DetalleCanjeProps {
  verCotizacion?: Cotizacion | null;
  isAlertOpenProductosProveedor: boolean;
  setCotizacionProductos: React.Dispatch<React.SetStateAction<any>>;
  descripcion: string;
  setDescripcion: React.Dispatch<React.SetStateAction<string>>;
  seleccion: SeleccionProductos;
  inventariosAutorizados: Set<number>;
  onToggleProducto: (p: FilaProductoCotizado) => void;
  onMarcarSeleccionados: () => void;
  proveedores: any[];
  recotizacion: Record<number, number[]>;
  onCambiarRecotizacion: (idInventario: number, ids: number[]) => void;
  onRecotizar: () => void;
  procesandoEnModal: boolean;
}

const CotizacionDatos = ({
  verCotizacion,
  seleccion,
  inventariosAutorizados,
  onToggleProducto,
  onMarcarSeleccionados,
  proveedores,
  recotizacion,
  onCambiarRecotizacion,
  onRecotizar,
  procesandoEnModal,
}: DetalleCanjeProps) => {
  const [proveedorModal] = useState<ProveedorCotizacion | null>(null);

  const esComputo = verCotizacion?.computo === "si";
  const estaActiva = Number(verCotizacion?.estatus) === ESTATUS_COTIZACION.ACTIVA;

  const motivoBloqueoCotizacion = estaActiva
    ? null
    : Number(verCotizacion?.estatus) === ESTATUS_COTIZACION.CONCLUIDA
      ? "La cotización está concluida"
      : "La cotización está cancelada";

  const proveedorPorInventario = useMemo(() => {
    const mapa: Record<number, string> = {};
    (verCotizacion?.proveedores ?? []).forEach((p) => {
      p.detalles?.forEach((d) => {
        const marcadoLocal = String(seleccion[d.id_inventario]) === String(d.id);
        const marcadoGuardado = Number(d.seleccionado) === 1;
        if (marcadoLocal || marcadoGuardado) {
          mapa[d.id_inventario] = p.nombre_comercial || p.razon_social;
        }
      });
    });
    return mapa;
  }, [verCotizacion?.proveedores, seleccion]);

  const proveedoresPorProducto = useMemo(() => {
    const mapa: Record<number, Set<number>> = {};
    (verCotizacion?.proveedores ?? []).forEach((p) =>
      p.detalles?.forEach((d) => {
        if (![1, 3].includes(Number(d.seleccionado))) return;
        const id = Number(d.id_inventario);
        (mapa[id] ??= new Set()).add(Number(p.id_proveedor));
      })
    );
    return mapa;
  }, [verCotizacion?.proveedores]);

  // Todos los proveedores que ya tienen el producto (respondieron o no)
  const proveedoresConDetallePorProducto = useMemo(() => {
    const mapa: Record<number, Set<number>> = {};
    (verCotizacion?.proveedores ?? []).forEach((p) =>
      p.detalles?.forEach((d) => {
        const id = Number(d.id_inventario);
        (mapa[id] ??= new Set()).add(Number(p.id_proveedor));
      })
    );
    return mapa;
  }, [verCotizacion?.proveedores]);

  const inventariosSinDisponibilidad = useMemo(
    () => obtenerInventariosSinDisponibilidad(verCotizacion),
    [verCotizacion]
  );

  const guardadosPorInventario = useMemo(() => {
    const mapa: Record<number, string> = {};
    (verCotizacion?.proveedores ?? []).forEach((p) =>
      p.detalles?.forEach((d) => {
        if (Number(d.seleccionado) === 1) mapa[Number(d.id_inventario)] = String(d.id);
      })
    );
    return mapa;
  }, [verCotizacion?.proveedores]);

  const proveedorAutorizadoPorInventario = useMemo(() => {
    const mapa: Record<number, string> = {};
    (verCotizacion?.proveedores ?? []).forEach((p) =>
      p.detalles?.forEach((d) => {
        if (Number(d.seleccionado) === 3) {
          mapa[Number(d.id_inventario)] = p.nombre_comercial || p.razon_social;
        }
      })
    );
    return mapa;
  }, [verCotizacion?.proveedores]);

  const cantidadSeleccionada = Object.keys(seleccion).length;

  const filasProveedores = useMemo(
    () => normalizarProveedoresCotizados(verCotizacion?.proveedores),
    [verCotizacion?.proveedores]
  );

  if (!verCotizacion) return null;

  const getEstadoColor = (
    estatus_cotizacion: number
  ): "info" | "warning" | "primary" | "default" | "error" | "success" => {
    switch (Number(estatus_cotizacion)) {
      case ESTATUS_COTIZACION.CANCELADA:
        return "error";
      case ESTATUS_COTIZACION.ACTIVA:
        return "info";
      case ESTATUS_COTIZACION.CONCLUIDA:
        return "success";
      default:
        return "default";
    }
  };

  const estadosMap: { [key: number]: string } = {
    [ESTATUS_COTIZACION.CANCELADA]: "Cancelada",
    [ESTATUS_COTIZACION.ACTIVA]: "Activa",
    [ESTATUS_COTIZACION.CONCLUIDA]: "Concluida",
  };

  const traducirEstado = (estado: number | null): string => {
    if (estado === null || estado === undefined) return "SIN ESTADO";
    return estadosMap[Number(estado)] ?? "SIN ESTADO";
  };

  // No cómputo: todos los productos de la requisición ya tienen proveedor seleccionado
  const seleccionCerrada =
    !esComputo &&
    (verCotizacion?.productos_requisicion?.length ?? 0) > 0 &&
    (verCotizacion?.productos_requisicion ?? []).every(
      (p) => guardadosPorInventario[Number(p.id_inventario)] !== undefined
    );
  const motivoBloqueoSeleccion =
    motivoBloqueoCotizacion ??
    (seleccionCerrada ? "Todos los productos ya fueron seleccionados" : null);

  return (
    <Box sx={{ px: 0, pb: 0, mt: 0 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2.5}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "flex-start" }}
            >
              {/* INFORMACIÓN DE LA EMPRESA */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2.5}
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Box
                  sx={{
                    width: 250,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={`${env.API_URL_ASSETS}logo_empresas/${verCotizacion?.empresa?.logo}`}
                    alt="Logo empresa"
                    sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                </Box>
                <Stack spacing={1.5} direction="row" sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      background: "#dedede",
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "5px !important",
                      p: 0.5,
                    }}
                  >
                    <IconBuilding size={35} stroke={2} />
                  </Box>

                  <Stack
                    direction="column"
                    sx={{
                      display: "flex",
                      alignItems: { xs: "center", sm: "flex-start" },
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", m: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        Empresa
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        noWrap
                        title={verCotizacion?.empresa?.nombre}
                      >
                        {verCotizacion?.empresa?.nombre}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", m: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        Razón social
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ wordBreak: "break-word" }}>
                        {verCotizacion?.empresa?.razon_social}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Divider sx={{ m: 1.5 }} />
            {/* INFORMACIÓN GENERAL DE PROVEEDORES QUE FUERON COTIZADOS */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 2, sm: 4 }}>
              <Contador
                color="#ff8513"
                icono={<IconBuildingStore size={35} stroke={2} />}
                etiqueta="Proveedores Cotizados"
                valor={verCotizacion.total_proveedores}
              />
              <Contador
                color="#1dad76"
                icono={<IconMessage2Plus size={35} stroke={2} />}
                etiqueta="Respondieron"
                valor={verCotizacion.proveedores_respondidos}
              />
              <Contador
                color="#bd0606"
                icono={<IconClock size={35} stroke={2} />}
                etiqueta="Sin respuesta"
                valor={verCotizacion.proveedores_pendientes}
              />
            </Stack>
          </Paper>
        </Grid>
        {/* DATOS DE LA REQUISICION */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Typography variant="h5" fontWeight={600}>
                Requisición
              </Typography>

              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <Chip
                  label={verCotizacion.requisicion}
                  sx={{ fontWeight: "bold", fontSize: 10, background: "#ebe1ae" }}
                />
                <Chip
                  label={traducirEstado(verCotizacion.estatus).toUpperCase()}
                  color={getEstadoColor(verCotizacion.estatus)}
                  sx={{ fontWeight: "bold", fontSize: 10 }}
                />
                <Chip
                  icon={
                    esComputo ? (
                      <IconDeviceDesktop size={16} stroke={2} />
                    ) : (
                      <IconPackage size={16} stroke={2} />
                    )
                  }
                  label={esComputo ? "EQUIPO DE CÓMPUTO" : "COTIZACIÓN GENERAL"}
                  sx={{
                    fontWeight: "bold",
                    fontSize: 10,
                    color: "#fff",
                    background: esComputo ? "#5e35b1" : "#0d9177",
                    "& .MuiChip-icon": { color: "#fff" },
                  }}
                />
              </Box>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatoRequisicion
                  icono={<IconUserEdit size={20} stroke={2} />}
                  etiqueta="Creada por"
                  valor={verCotizacion.nombre_creador}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatoRequisicion
                  icono={<IconUser size={20} stroke={2} />}
                  etiqueta="Solicitante"
                  valor={verCotizacion.nombre_usuario}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatoRequisicion
                  icono={<IconCalendarEvent size={20} stroke={2} />}
                  etiqueta="Creación de la requisición"
                  valor={formatearFecha(verCotizacion.fecha_creacion)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatoRequisicion
                  icono={<IconCalendarTime size={20} stroke={2} />}
                  etiqueta="Creación de la cotización"
                  valor={formatearFecha(verCotizacion.fecha)}
                />
              </Grid>
            </Grid>
            {/* OBSERVACIONES DE LA COTIZACIÓN */}
            {verCotizacion.observaciones && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  borderRadius: 2,
                  background: "#f8fafc",
                  border: "1px dashed #d0dce6",
                  textAlign: "left",
                }}
              >
                <Box
                  sx={{
                    background: "#eef3f8",
                    color: "#084d6e",
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "5px",
                  }}
                >
                  <IconNotes size={20} stroke={2} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Observaciones de la cotización
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={400}
                    color={"text.secondary"}
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontStyle: "italic",
                    }}
                  >
                    {verCotizacion.observaciones?.trim() || "Sin observaciones"}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <ProductosRequisicionList
            editable={estaActiva}
            seleccionCerrada={seleccionCerrada}
            productos={verCotizacion.productos_requisicion ?? []}
            esComputo={esComputo}
            proveedorPorInventario={proveedorPorInventario}
            cantidadSeleccionada={cantidadSeleccionada}
            onMarcarSeleccionados={onMarcarSeleccionados}
            proveedores={proveedores}
            proveedoresPorProducto={proveedoresPorProducto}
            recotizacion={recotizacion}
            onCambiarRecotizacion={onCambiarRecotizacion}
            onRecotizar={onRecotizar}
            procesando={procesandoEnModal}
            proveedorAutorizadoPorInventario={proveedorAutorizadoPorInventario}
            recotizacionHabilitada={estaActiva && !verCotizacion.tiene_ordenes_compra}
            inventariosSinDisponibilidad={inventariosSinDisponibilidad}
            proveedoresConDetallePorProducto={proveedoresConDetallePorProducto}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <CotizacionProveedoresTabla
            filas={filasProveedores}
            esComputo={esComputo}
            seleccion={seleccion}
            inventariosAutorizados={inventariosAutorizados}
            onToggleProducto={onToggleProducto}
            motivoBloqueoCotizacion={motivoBloqueoSeleccion}
            guardadosPorInventario={guardadosPorInventario}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CotizacionDatos;